import React from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'ha/helpers/router';
import Helmet from 'react-helmet';

import { Language } from '@ha/intl';
import { useIntl } from 'ha/i18n';
import { checkSearchQueryEmpty } from 'ha/utils/checkSearchQueryEmpty';
import { LocaleToCanonicalCollections } from 'ha/constants/SearchPageCollections';

import {
  getlangURLListState,
  getNumberOfListings,
  getAdvertiserName,
  getIsSearchEmpty,
  getCountryCanonical,
  getCachedUpdateSearch,
} from '../selectors';
import { getCollection } from '../selectors/filterSelectors';
import { shouldCountryBeIndexed } from '../utils';

const MinNumOfListingsForIndex = 5;

const getCollectionPath = (key: Language, canonicalCollection?: string) =>
  canonicalCollection
    ? `/${LocaleToCanonicalCollections[key][canonicalCollection]}`
    : '';

const SearchPageCrawl: React.FC = () => {
  const { lang, urlResolver } = useIntl();
  const { query, pathname } = useLocation();

  const { page } = query;

  const langUrlList: { [key in Language]?: string } =
    useSelector(getlangURLListState);
  const numberOfListings = useSelector(getNumberOfListings);
  const isPageEmpty = useSelector(getIsSearchEmpty);
  const canonicalCollection = useSelector(getCollection);
  const advertiserName = useSelector(getAdvertiserName);
  const canonicalCountryName = useSelector(getCountryCanonical);
  const cachedUpdateSearch = useSelector(getCachedUpdateSearch);

  const isQueryEmpty = checkSearchQueryEmpty(query);
  const localeUrl = langUrlList[lang];
  const translatedPathname = `${localeUrl}${getCollectionPath(
    lang,
    canonicalCollection,
  )}`;
  const translatedPathnameIsDifferent =
    localeUrl &&
    decodeURIComponent(pathname) !== decodeURIComponent(translatedPathname);

  const pageQuery = page && page !== '1' ? `?page=${page}` : '';
  const isCountryWhitelisted = shouldCountryBeIndexed(canonicalCountryName);

  const setNoIndex =
    isPageEmpty ||
    // index only first page
    pageQuery ||
    numberOfListings < MinNumOfListingsForIndex ||
    !isQueryEmpty ||
    translatedPathnameIsDifferent ||
    advertiserName ||
    !isCountryWhitelisted ||
    cachedUpdateSearch.geonameId === 0;

  return setNoIndex ? (
    <Helmet>
      <meta name="robots" content="noindex" />
    </Helmet>
  ) : (
    <Helmet>
      <link
        rel="canonical"
        href={urlResolver.absolutizePath(`${translatedPathname}${pageQuery}`, {
          skipAddLanguage: true,
        })}
      />
      {langUrlList.en && (
        <link
          rel="alternate"
          hrefLang="x-default"
          href={urlResolver.absolutizePath(
            `${langUrlList.en}${getCollectionPath(
              Language.en,
              canonicalCollection,
            )}${pageQuery}`,
            { skipAddLanguage: true },
          )}
        />
      )}
      {Object.keys(langUrlList).map(
        (key: Language) =>
          langUrlList[key] && (
            <link
              rel="alternate"
              key={key}
              hrefLang={key}
              href={urlResolver.absolutizePath(
                `${langUrlList[key]}${getCollectionPath(
                  key,
                  canonicalCollection,
                )}${pageQuery}`,
                { skipAddLanguage: true },
              )}
            />
          ),
      )}
    </Helmet>
  );
};

export default SearchPageCrawl;
